import { SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Tooltip,
} from '@chakra-ui/react';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './style.css';

interface InputSearchProps<T> {
  callback: (_value: string) => Promise<{ data: T[] }>;
  onSelect: (_file: T) => void;
  disabled: boolean;
}

type DefaultRecord = {
  id: string;
  name: string;
};

const InputSearch = <T extends DefaultRecord>({
  callback,
  onSelect,
  disabled,
}: InputSearchProps<T>) => {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [result, setResult] = useState<T[]>([]);
  const [value, setValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFocus, setIsFocus] = useState(false);

  const isShowMenu = (result && result.length > 0 && value && isFocus) || false;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounced = useCallback(
    debounce(async (value) => {
      setIsLoading(true);
      const res = await callback(value);
      if (res?.data) {
        setResult(res?.data);
      }
      setIsLoading(false);
    }, 500),
    []
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setValue(value);
    debounced(value);
  };

  const onClickMenuItem = (item: T) => {
    onSelect(item);
    setIsFocus(false);
  };

  const onFocus = () => {
    setIsFocus(true);
  };

  useEffect(() => {
    const handleClickOutside = (e: Event) => {
      if (!(menuRef?.current?.contains(e.target as Node) || inputRef?.current === e.target)) {
        setIsFocus(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Box>
      <InputGroup>
        <InputLeftElement pointerEvents='none'>
          <SearchIcon color='gray.300' />
        </InputLeftElement>
        <Input
          ref={inputRef}
          disabled={disabled}
          onChange={handleChange}
          onFocus={onFocus}
          placeholder={t('summary.searchPlaceholder')}
          value={value}
        />
        {isLoading && (
          <>
            <InputRightElement>
              <Spinner thickness='3px' />
            </InputRightElement>
          </>
        )}
      </InputGroup>
      <Menu isOpen={isShowMenu}>
        <MenuButton as={Box} value={value} />
        <MenuList ref={menuRef}>
          {result?.map((item) => (
            <MenuItem key={item?.id} onClick={() => onClickMenuItem(item)}>
              <Tooltip label={item?.name}>
                <Box
                  overflow='hidden'
                  maxW={'440px'}
                  me={4}
                  whiteSpace={'nowrap'}
                  textOverflow='ellipsis'
                >
                  {item?.name}
                </Box>
              </Tooltip>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  );
};

export default InputSearch;
